<template>
  <AppLayout>
    <Head :title="`Aktywność — ${board.name}`" />
    <div class="col-12 layout-spacing">
      <div class="widget widget-six">
        <div class="widget-heading">
          <div class="row align-items-center">
            <div class="col-12 col-lg-7">
              <Link :href="`/kanban/boards-all/${board.id}`" class="text-decoration-none small">
                <i class="bi bi-arrow-left me-1"></i>
                Wróć do tablicy
              </Link>
              <h4 class="mt-3 mb-2">
                <i class="bi bi-clock-history me-2"></i>
                Historia aktywności
              </h4>
              <p class="text-muted mb-0">
                Tablica: {{ board.name }}
              </p>
            </div>
            <div class="col-12 col-lg-5 mt-3 mt-lg-0 text-lg-end">
              <Link :href="`/kanban/boards-all/${board.id}`" class="btn btn-outline-primary btn-sm">
                <i class="bi bi-kanban me-1"></i>
                Otwórz tablicę
              </Link>
            </div>
          </div>
        </div>

        <div class="widget-content">
          <div class="custom-table">
            <div v-if="!activities.data?.length" class="text-center py-5">
              <i class="bi bi-clock-history activity-empty-icon"></i>
              <h5 class="mt-3">
                Brak aktywności
              </h5>
              <p class="text-muted mb-0">
                Nie ma jeszcze żadnych zdarzeń do wyświetlenia.
              </p>
            </div>
            <div v-else class="activity-timeline">
              <article
                v-for="activity in activities.data"
                :key="activity.id"
                class="activity-item">
                <div class="activity-avatar">
                  {{
                    (activity.user?.name ?? '?')
                      .charAt(0)
                      .toUpperCase()
                  }}
                </div>

                <div class="activity-content">
                  <div class="d-flex flex-wrap align-items-center gap-2">
                    <span class="fw-semibold">
                      {{ activity.user?.name ?? 'Użytkownik' }}
                    </span>
                    <span class="text-muted">
                      {{ eventLabel(activity.event) }}
                    </span>
                    <span v-if="activity.card" class="badge bg-light text-dark activity-card-title">
                      <i class="bi bi-card-text me-1"></i>
                      {{ activity.card.title }}
                    </span>
                  </div>

                  <p class="text-muted small mt-2 mb-3">
                    <i class="bi bi-calendar3 me-1"></i>
                    {{ formatDate(activity.created_at) }}
                  </p>

                  <div v-if="activity.event === 'card.updated' &&
					  Object.keys(getActivityProperties(activity)).length" class="activity-changes">
                    <div v-for="(change, field) in getActivityProperties(activity)" :key="field" class="activity-change">
                      <span class="fw-semibold">
                        {{ fieldLabel(field) }}:
                      </span>

                      <div class="d-flex flex-wrap align-items-center gap-2 mt-1">
                        <span class="activity-old-value">
                          {{ formatValue(field, change.old) }}
                        </span>
                        <i class="bi bi-arrow-right text-muted"></i>
                        <span class="activity-new-value">
                          {{ formatValue(field, change.new) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="activity.event === 'assignees.updated'"
                    class="activity-info"
                  >
                    <i class="bi bi-people me-2"></i>
                    Zaktualizowano listę osób przypisanych do karty.
                  </div>

                  <!-- Zmiana etykiet -->
                  <div
                    v-if="activity.event === 'labels.updated'"
                    class="activity-info"
                  >
                    <i class="bi bi-tags me-2"></i>
                    Zaktualizowano etykiety karty.
                  </div>

                  <!-- Utworzenie kolumny -->
                  <div
                    v-if="activity.event === 'column.created'"
                    class="activity-info"
                  >
                    <i class="bi bi-layout-three-columns me-2"></i>

                    Kolumna:

                    <span class="fw-semibold ms-1">
                      {{
                        activity.properties?.column_name ??
                        'Nowa kolumna'
                      }}
                    </span>
                  </div>

                </div>
              </article>

            </div>
          </div>

          <!-- Paginacja -->
          <nav
            v-if="activities.links?.length > 3"
            class="mt-4"
            aria-label="Stronicowanie historii aktywności"
          >
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">

              <p class="text-muted small mb-0">
                Strona
                {{ activities.current_page ?? 1 }}
                <span v-if="activities.last_page">
                  z {{ activities.last_page }}
                </span>
              </p>

              <div class="d-flex flex-wrap gap-1">

                <template
                  v-for="(link, index) in activities.links"
                  :key="index"
                >
                  <Link
                    v-if="link.url"
                    :href="link.url"
                    preserve-scroll
                    class="btn btn-sm"
                    :class="link.active
                      ? 'btn-primary'
                      : 'btn-outline-secondary'"
                    v-html="link.label"
                  />

                  <span
                    v-else
                    class="btn btn-sm btn-outline-secondary disabled"
                    v-html="link.label"
                  />
                </template>

              </div>
            </div>
          </nav>

        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/Layouts/App.vue'
import { Head, Link } from '@inertiajs/vue3'

export default {
  components: {
    AppLayout,
    Head,
    Link,
  },

  props: {
    board: {
      type: Object,
      required: true,
    },

    activities: {
      type: Object,
      required: true,
    },
  },

  methods: {
  getActivityProperties(activity) {
  let properties = activity.properties

  if (typeof properties === 'string') {
    try {
      properties = JSON.parse(properties)
    } catch {
      return {}
    }
  }

  if (!properties || typeof properties !== 'object') {
    return {}
  }

  return properties
},
    eventLabel(event) {
      const labels = {
        'card.created': 'utworzył(a) kartę',
        'card.updated': 'edytował(a) kartę',
        'card.moved': 'przeniósł/przeniosła kartę',
        'comment.created': 'dodał(a) komentarz',
        'assignees.updated': 'zmienił(a) osoby przypisane',
        'labels.updated': 'zmienił(a) etykiety karty',
        'column.created': 'utworzył(a) kolumnę',
        'label.created': 'utworzył(a) etykietę',
      }

      return labels[event] ?? String(event ?? '').replace(/\./g, ' ')
    },

    fieldLabel(field) {
      const labels = {
        title: 'Tytuł',
        description: 'Opis',
        priority: 'Priorytet',
        start_at: 'Data rozpoczęcia',
        due_at: 'Termin',
      }

      return labels[field] ?? field
    },

    formatValue(field, value) {
      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        return '—'
      }

      if (field === 'priority') {
        const priorities = {
          low: 'Niski',
          normal: 'Normalny',
          high: 'Wysoki',
          urgent: 'Pilny',
        }

        return priorities[value] ?? value
      }

      if (['start_at', 'due_at'].includes(field)) {
        return this.formatDate(value)
      }

      if (field === 'description') {
        const description = String(value)

        return description.length > 100
          ? `${description.slice(0, 100)}…`
          : description
      }

      return String(value)
    },

    formatDate(value) {
      if (!value) return ''

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return ''
      }

      return new Intl.DateTimeFormat('pl-PL', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
    },
  },
}
</script>

<style scoped>
.activity-timeline {
  display: flex;
  flex-direction: column;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid #e9ecef;
}

.activity-item:first-child {
  padding-top: 0.5rem;
}

.activity-item:last-child {
  border-bottom: 0;
}

.activity-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e9f2ff;
  color: #0d6efd;
  font-size: 1rem;
  font-weight: 600;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-card-title {
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: normal;
}

.activity-changes {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.activity-change {
  padding: 0.75rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
  overflow-wrap: anywhere;
}

.activity-old-value {
  padding: 0.25rem 0.5rem;
  border-radius: 5px;
  background: #f8d7da;
  color: #842029;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.activity-new-value {
  padding: 0.25rem 0.5rem;
  border-radius: 5px;
  background: #d1e7dd;
  color: #0f5132;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.activity-info {
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: #f8f9fa;
  color: #495057;
  font-size: 0.875rem;
}

.activity-empty-icon {
  font-size: 2.5rem;
  color: #adb5bd;
}

@media (max-width: 575.98px) {
  .activity-item {
    gap: 0.75rem;
  }

  .activity-avatar {
    width: 36px;
    height: 36px;
    font-size: 0.875rem;
  }
}
</style>